import React, { useState, useRef } from 'react';
import { ImportType } from '../../types/csvImport';
import { downloadCSVTemplate, getCSVTemplate } from '../../services/api';

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  importType: ImportType;
  onImport: (file: File) => void;
  isImporting: boolean;
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  importType,
  onImport,
  isImporting
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [step, setStep] = useState<'download' | 'upload'>('download');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        onImport(file);
      } else {
        alert('Please select a CSV file');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      onImport(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadTemplate = async () => {
    try {
      const template = await getCSVTemplate(importType);
      downloadCSVTemplate(template.csv_content, template.filename);
      setStep('upload');
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  const getImportTypeInfo = () => {
    switch (importType) {
      case ImportType.FUNCTIONS:
        return {
          title: 'Bulk Import Critical Functions',
          description: 'Download the template, fill it with your data, then upload it back',
          requiredFields: ['name', 'description', 'criticality', 'max_downtime', 'responsible_role', 'responsible_team', 'rto', 'rpo', 'owner'],
          sampleData: 'Customer Service Operations,Handling customer inquiries,critical,2 hours,Customer Service Manager,Customer Service,Phone system;CRM,IT systems;Staff,Activate backup call center,1 hour,15 minutes,John Smith'
        };
      case ImportType.SUPPLIERS:
        return {
          title: 'Bulk Import Key Suppliers',
          description: 'Download the template, fill it with your data, then upload it back',
          requiredFields: ['name'],
          sampleData: 'ABC IT Services,Jane Doe,+1-555-0123,jane@abcitservices.com,IT support and maintenance,high,XYZ Tech;IT Solutions Inc,4 hours,high'
        };
      case ImportType.IT_SYSTEMS:
        return {
          title: 'Bulk Import IT Systems',
          description: 'Download the template, fill it with your data, then upload it back',
          requiredFields: ['name'],
          sampleData: 'Customer Database,Primary customer database,critical,1 hour,IT Operations,AWS RDS Multi-AZ,Failover to standby,30 minutes,5 minutes,database,Amazon Web Services,Customer Service;Sales,Network;Auth,1'
        };
      default:
        return {
          title: 'Bulk Import Data',
          description: 'Download the template, fill it with your data, then upload it back',
          requiredFields: [],
          sampleData: ''
        };
    }
  };

  const info = getImportTypeInfo();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block w-full max-w-2xl p-6 my-8 text-left align-middle bg-white rounded-2xl shadow-xl transition-all transform">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{info.title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step === 'download' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}`}>
                <span className="text-sm font-medium">1</span>
              </div>
              <div className={`w-16 h-1 ${step === 'upload' ? 'bg-green-600' : 'bg-gray-300'}`}></div>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step === 'upload' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                <span className="text-sm font-medium">2</span>
              </div>
            </div>
          </div>

          {step === 'download' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">Step 1: Download Template</h4>
                <p className="text-gray-600 mb-6">{info.description}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-2">Required Fields:</h5>
                <div className="flex flex-wrap gap-2">
                  {info.requiredFields.map((field, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      {field}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-2">Sample Data:</h5>
                <code className="text-xs text-gray-600 bg-white p-2 rounded block overflow-x-auto">
                  {info.sampleData}
                </code>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleDownloadTemplate}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  📥 Download Template
                </button>
              </div>
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">Step 2: Upload Filled Template</h4>
                <p className="text-gray-600 mb-6">Upload your completed CSV file to import all data at once</p>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 text-gray-400">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-900">
                      Drop your CSV file here, or{' '}
                      <button
                        onClick={handleClick}
                        className="text-blue-600 hover:text-blue-500 underline"
                      >
                        browse
                      </button>
                    </p>
                    <p className="text-sm text-gray-500">CSV files only</p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setStep('download')}
                  className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  ← Back to Download
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isImporting && (
            <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-2xl">
              <div className="text-center">
                <div className="mx-auto mb-4 w-8 h-8 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
                <p className="text-gray-600">Importing data...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
